import { useMemo } from "react";
import { useApp } from "@/contexts/AppContext";
import Topbar from "@/components/layout/Topbar";

const fmt = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : "0");

function normalize(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function tokenOverlap(a, b) {
  const A = new Set(normalize(a).split(" ").filter((t) => t.length >= 3));
  const B = new Set(normalize(b).split(" ").filter((t) => t.length >= 3));
  if (!A.size || !B.size) return 0;
  let hit = 0;
  A.forEach((t) => { if (B.has(t)) hit += 1; });
  return hit / Math.min(A.size, B.size);
}

// --- Recommendation builders (one per recommendation type) ---

function findDuplicatesAndNamingIssues(rows) {
  const recs = [];
  const byCat = new Map();
  rows.forEach((r) => {
    if (!byCat.has(r.Category)) byCat.set(r.Category, []);
    byCat.get(r.Category).push(r);
  });
  const seen = new Set();
  byCat.forEach((items) => {
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i], b = items[j];
        if (a.SKU === b.SKU) continue;
        const key = [a.SKU, b.SKU].sort().join("|");
        if (seen.has(key)) continue;
        const score = tokenOverlap(a.ItemName, b.ItemName);
        const aNorm = normalize(a.ItemName);
        if (score >= 0.6 && aNorm !== "" && aNorm === normalize(b.ItemName)) {
          seen.add(key);
          recs.push({
            RecommendationType: "Consolidate duplicate SKUs",
            SKU: `${a.SKU} ↔ ${b.SKU}`,
            ItemName: a.ItemName,
            Site: a.Site === b.Site ? a.Site : `${a.Site}, ${b.Site}`,
            Category: a.Category,
            SupplierName: a.SupplierName === b.SupplierName
              ? a.SupplierName : `${a.SupplierName} / ${b.SupplierName}`,
            Reason: "Identical item name across multiple SKUs",
            EstimatedValueImpact: (a.InventoryValue || 0) + (b.InventoryValue || 0),
          });
        } else if (score >= 0.7 && a.SupplierName === b.SupplierName) {
          seen.add(key);
          recs.push({
            RecommendationType: "Standardize supplier/item naming",
            SKU: `${a.SKU} ↔ ${b.SKU}`,
            ItemName: `${a.ItemName} / ${b.ItemName}`,
            Site: a.Site === b.Site ? a.Site : `${a.Site}, ${b.Site}`,
            Category: a.Category,
            SupplierName: a.SupplierName,
            Reason: "Similar names, same supplier & category — likely duplicate",
            EstimatedValueImpact: (a.InventoryValue || 0) + (b.InventoryValue || 0),
          });
        }
      }
    }
  });
  return recs;
}

function findObsoleteItems(rows) {
  return rows
    .filter((r) => r.OverstockQty > 0 && r.AverageMonthlyUsage < 2)
    .map((r) => ({
      RecommendationType: "Review obsolete items",
      SKU: r.SKU, ItemName: r.ItemName, Site: r.Site,
      Category: r.Category, SupplierName: r.SupplierName,
      Reason: `Overstocked (${fmt(r.OverstockQty)} excess) with low usage (${fmt(r.AverageMonthlyUsage)}/mo)`,
      EstimatedValueImpact: r.OverstockValue || 0,
    }));
}

function findExcessSafetyStock(rows) {
  return rows
    .filter((r) => r.Criticality === "Low" && r.MaxStock > 0 && r.CurrentStock > r.MaxStock * 1.5)
    .map((r) => ({
      RecommendationType: "Reduce excess safety stock",
      SKU: r.SKU, ItemName: r.ItemName, Site: r.Site,
      Category: r.Category, SupplierName: r.SupplierName,
      Reason: "Low-criticality item stocked well above max threshold",
      EstimatedValueImpact: r.OverstockValue || 0,
    }));
}

function findSlowMovingItems(rows, slowDays) {
  return rows
    .filter((r) => r.SlowMoving)
    .map((r) => ({
      RecommendationType: "Slow-moving: consider rationalizing",
      SKU: r.SKU, ItemName: r.ItemName, Site: r.Site,
      Category: r.Category, SupplierName: r.SupplierName,
      Reason: `No movement in ${slowDays}+ days or negligible usage`,
      EstimatedValueImpact: r.InventoryValue || 0,
    }));
}

function findRebalanceOpportunities(rows) {
  const bySku = new Map();
  rows.forEach((r) => {
    if (!bySku.has(r.SKU)) bySku.set(r.SKU, []);
    bySku.get(r.SKU).push(r);
  });
  const recs = [];
  bySku.forEach((list, sku) => {
    if (list.length < 2) return;
    const overs = list.filter((r) => r.OverstockQty > 0);
    const outs = list.filter((r) => r.StockoutRisk === "High" || r.StockoutRisk === "Medium");
    if (!overs.length || !outs.length) return;
    recs.push({
      RecommendationType: "Rebalance stock across sites",
      SKU: sku, ItemName: list[0].ItemName,
      Site: `${overs[0].Site} → ${outs[0].Site}`,
      Category: list[0].Category, SupplierName: list[0].SupplierName,
      Reason: `Overstocked at ${overs[0].Site}, stockout risk at ${outs[0].Site}`,
      EstimatedValueImpact: Math.min(overs[0].OverstockValue || 0, outs[0].ReorderValue || 0),
    });
  });
  return recs;
}

function buildRecommendations(rows, settings) {
  const all = [
    ...findDuplicatesAndNamingIssues(rows),
    ...findObsoleteItems(rows),
    ...findExcessSafetyStock(rows),
    ...findSlowMovingItems(rows, settings.slowMovingDays),
    ...findRebalanceOpportunities(rows),
  ];
  return all.sort((a, b) => (b.EstimatedValueImpact || 0) - (a.EstimatedValueImpact || 0));
}

const recKey = (r) => `${r.RecommendationType}|${r.SKU}|${r.Site || ""}`;

const TYPE_CLASS = {
  "Consolidate duplicate SKUs": "text-sky-700 bg-sky-50 border-sky-200",
  "Review obsolete items": "text-red-700 bg-red-50 border-red-200",
  "Reduce excess safety stock": "text-amber-700 bg-amber-50 border-amber-200",
  "Standardize supplier/item naming": "text-slate-700 bg-slate-100 border-slate-200",
  "Rebalance stock across sites": "text-emerald-700 bg-emerald-50 border-emerald-200",
  "Slow-moving: consider rationalizing": "text-slate-700 bg-slate-100 border-slate-200",
};

export default function SkuRationalization() {
  const { forecast, settings } = useApp();
  const recs = useMemo(
    () => buildRecommendations(forecast.rows, settings),
    [forecast.rows, settings]
  );
  // Note: buildRecommendations is module-scope (stable identity, never changes
  // across renders) so it does not need to be in the dep array.
  const cur = settings.currency;
  const totalImpact = recs.reduce((s, r) => s + (r.EstimatedValueImpact || 0), 0);

  return (
    <>
      <Topbar title="SKU Rationalization" subtitle="Consolidation, cleanup, and rebalancing opportunities." />
      <div className="flex-1 px-8 py-7 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="kpi-recommendations">
            <div className="text-[11px] uppercase tracking-[0.1em] text-slate-500 font-semibold">Total Recommendations</div>
            <div className="font-mono text-[26px] font-semibold mt-3 text-slate-900">{recs.length}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="kpi-impact">
            <div className="text-[11px] uppercase tracking-[0.1em] text-slate-500 font-semibold">Estimated Value Impact</div>
            <div className="font-mono text-[26px] font-semibold mt-3 text-slate-900">{cur}{fmt(totalImpact)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="kpi-unique-types">
            <div className="text-[11px] uppercase tracking-[0.1em] text-slate-500 font-semibold">Recommendation Types</div>
            <div className="font-mono text-[26px] font-semibold mt-3 text-slate-900">{new Set(recs.map((r) => r.RecommendationType)).size}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg" data-testid="rationalization-table">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="font-heading font-semibold text-slate-900">Rationalization Opportunities</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>SKU</th>
                  <th>Item</th>
                  <th>Site</th>
                  <th>Category</th>
                  <th>Supplier</th>
                  <th>Reason</th>
                  <th className="num">Est. Impact</th>
                </tr>
              </thead>
              <tbody>
                {recs.map((r) => (
                  <tr key={recKey(r)}>
                    <td>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${TYPE_CLASS[r.RecommendationType] || "text-slate-700 bg-slate-100 border-slate-200"}`}>
                        {r.RecommendationType}
                      </span>
                    </td>
                    <td className="font-mono text-slate-900">{r.SKU}</td>
                    <td className="text-slate-700">{r.ItemName}</td>
                    <td className="text-slate-500">{r.Site}</td>
                    <td className="text-slate-500">{r.Category}</td>
                    <td className="text-slate-500">{r.SupplierName}</td>
                    <td className="text-slate-600">{r.Reason}</td>
                    <td className="num">{cur}{fmt(r.EstimatedValueImpact)}</td>
                  </tr>
                ))}
                {recs.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-sm text-slate-500 py-10">No rationalization opportunities detected.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
